import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { NgClass } from '@angular/common'
import { Component, DestroyRef, effect, input, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms'
import { EventService } from '@app/v2/event/services/event.service'
import { SocialCollection, SocialMediaCard } from '@app/v2/event/types/event-social-media.type'
import { Event } from '@app/v2/event/types/event.type'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { ComponentWithDataSetter, DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subject } from 'rxjs'
import { finalize, takeUntil } from 'rxjs/operators'
import { SocialMediaCardComponent } from '../social-media-card/social-media-card.component'
import { SocialMediaCollectionsDropdownComponent } from '../social-media-collections-dropdown/social-media-collections-dropdown.component'

enum UpdateCollectionDropdownOptions {
  UPDATE = 'update',
  ADD = 'add'
}

@Component({
  selector: 'app-social-media-links',
  templateUrl: './social-media-links.component.html',
  styleUrls: ['./social-media-links.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    BasicInputComponent,
    NgxTippyModule,
    DragDropModule,
    SocialMediaCardComponent,
    DropdownComponent,
    ModalComponent,
    ButtonSpinnerDirective,
    NgClass,
    DeleteModalComponent
  ]
})
export class SocialMediaLinksComponent {
  public event = input<Event | null>(null)
  public isLoadingEvent = input<boolean>(false)

  public linkControl = new FormControl('', [this._urlValidator()])
  public readonly linkEmptyIcon = `<svg width="18" height="18" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.73913 28C6.44023 28.0021 5.16997 27.5834 4.08939 26.7968C3.00881 26.0101 2.16655 24.8911 1.6694 23.5814C1.17224 22.2718 1.04256 20.8306 1.2968 19.4404C1.55104 18.0502 2.17775 16.7738 3.09749 15.7728L8.31969 10.075C9.55169 8.73343 11.221 7.98002 12.9613 7.98002C14.7017 7.98002 16.371 8.73343 17.603 10.075C17.8509 10.3635 17.9862 10.746 17.9804 11.1415C17.9745 11.537 17.8278 11.9145 17.5714 12.1941C17.3151 12.4738 16.9691 12.6335 16.6067 12.6397C16.2442 12.6458 15.8939 12.4978 15.6296 12.2271C14.9212 11.4562 13.9617 11.0234 12.9613 11.0234C11.961 11.0234 11.0014 11.4562 10.293 12.2271L5.07082 17.9264C4.36313 18.6987 3.96556 19.7462 3.96556 20.8385C3.96556 21.3793 4.06317 21.9149 4.25281 22.4145C4.44245 22.9142 4.72041 23.3682 5.07082 23.7506C5.42122 24.133 5.83722 24.4364 6.29505 24.6434C6.75288 24.8503 7.24358 24.9568 7.73913 24.9568C8.73995 24.9568 9.69977 24.5229 10.4074 23.7506L11.0843 23.0119C11.2143 22.8705 11.3686 22.7584 11.5383 22.6821C11.708 22.6058 11.8899 22.5666 12.0734 22.567C12.257 22.5674 12.4387 22.6072 12.6082 22.6842C12.7776 22.7611 12.9315 22.8738 13.0611 23.0157C13.1907 23.1576 13.2934 23.326 13.3633 23.5112C13.4333 23.6964 13.4691 23.8949 13.4688 24.0952C13.4685 24.2956 13.432 24.4939 13.3614 24.6788C13.2909 24.8638 13.1877 25.0317 13.0576 25.1732L12.3808 25.9118C11.7712 26.5764 11.0474 27.1029 10.2508 27.4613C9.45428 27.8196 8.6007 28.0027 7.73913 28Z" fill="#6B7280"/><path d="M15.0561 20.0221C14.194 20.0236 13.3402 19.8392 12.5437 19.4796C11.7471 19.1199 11.0235 18.5921 10.4144 17.9264C10.1602 17.6391 10.0195 17.2544 10.0227 16.8551C10.0259 16.4557 10.1727 16.0737 10.4314 15.7913C10.6902 15.5089 11.0402 15.3488 11.4061 15.3453C11.772 15.3418 12.1245 15.4953 12.3878 15.7728C13.1066 16.5217 14.0622 16.9396 15.0561 16.9396C16.0499 16.9396 17.0055 16.5217 17.7244 15.7728L22.9452 10.075C23.2963 9.69295 23.5748 9.23907 23.7649 8.73936C23.9549 8.23964 24.0527 7.70391 24.0527 7.16285C24.0527 6.6218 23.9549 6.08607 23.7649 5.58635C23.5748 5.08663 23.2963 4.63276 22.9452 4.25075C22.2267 3.50156 21.2713 3.08349 20.2776 3.08349C19.2839 3.08349 18.3284 3.50156 17.61 4.25075L16.5689 5.38696C16.307 5.67254 15.9519 5.8329 15.5817 5.83276C15.2115 5.83262 14.8565 5.67198 14.5948 5.38619C14.3332 5.1004 14.1862 4.71287 14.1864 4.30884C14.1865 3.90482 14.3337 3.5174 14.5955 3.23181L15.6366 2.0956C16.8677 0.753096 18.5369 -0.000713585 20.2772 5.06887e-07C22.0175 0.000714598 23.6863 0.755894 24.9164 2.09941C26.1465 3.44292 26.8372 5.26472 26.8366 7.16402C26.8359 9.06333 26.144 10.8846 24.9129 12.2271L19.6907 17.9249C19.0826 18.59 18.3602 19.1176 17.5649 19.4775C16.7696 19.8374 15.917 20.0225 15.0561 20.0221Z" fill="#6B7280"/></svg>`
  public readonly addIcon = '<i class="fas fa-plus tw-text-gray-900 tw-text-lg"></i>'
  public basicInputMode = BasicInputMode
  public links = signal<SocialMediaCard[]>([])
  public updateCollectionDropdownOptions: DropdownOption[] = [
    {
      key: UpdateCollectionDropdownOptions.UPDATE,
      value: 'Update current collection',
      prefixIcon:
        '<svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.1204 6.3C11.7261 6.3 11.4074 6.6136 11.4074 7C11.4074 9.702 9.16799 11.9 6.41667 11.9C5.09911 11.9 3.85214 11.382 2.93099 10.5H4.99074C5.38501 10.5 5.7037 10.1864 5.7037 9.8C5.7037 9.4136 5.38501 9.1 4.99074 9.1H1.60631C1.46514 9.0713 1.31756 9.0881 1.1771 9.149C1.16427 9.1539 1.15072 9.156 1.13789 9.1616C1.12862 9.1665 1.11864 9.1658 1.10937 9.1707C1.09012 9.1819 1.07943 9.2001 1.06089 9.212C0.856268 9.3359 0.712963 9.5473 0.712963 9.8V13.3C0.712963 13.6864 1.03166 14 1.42593 14C1.82019 14 2.13889 13.6864 2.13889 13.3V11.676C3.30102 12.6987 4.81606 13.3 6.41667 13.3C9.9551 13.3 12.8333 10.4734 12.8333 7C12.8333 6.6136 12.5146 6.3 12.1204 6.3Z" fill="#1C64F2"/><path d="M1.42593 7C1.42593 4.298 3.66534 2.1 6.41667 2.1C7.73422 2.1 8.98119 2.618 9.90234 3.5H7.84259C7.44832 3.5 7.12963 3.8136 7.12963 4.2C7.12963 4.5864 7.44832 4.9 7.84259 4.9H11.2256C11.3675 4.9294 11.5165 4.9119 11.6569 4.8503C11.6698 4.8454 11.6833 4.8433 11.6962 4.8377C11.7054 4.8328 11.7154 4.8335 11.7247 4.8286C11.7439 4.8174 11.7546 4.7992 11.7732 4.7873C11.9771 4.6641 12.1204 4.4527 12.1204 4.2V0.7C12.1204 0.3136 11.8017 0 11.4074 0C11.0131 0 10.6944 0.3136 10.6944 0.7V2.324C9.53232 1.3013 8.01727 0.7 6.41667 0.7C2.87823 0.7 0 3.5266 0 7C0 7.3864 0.318694 7.7 0.712963 7.7C1.10723 7.7 1.42593 7.3864 1.42593 7Z" fill="#1C64F2"/></svg>'
    },
    {
      key: UpdateCollectionDropdownOptions.ADD,
      value: 'Save as new collection',
      prefixIcon:
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.2222 6.22222H7.77778V0.777778C7.77778 0.571498 7.69583 0.373667 7.54997 0.227806C7.40411 0.0819442 7.20628 0 7 0C6.79372 0 6.59589 0.0819442 6.45003 0.227806C6.30417 0.373667 6.22222 0.571498 6.22222 0.777778V6.22222H0.777778C0.571498 6.22222 0.373667 6.30417 0.227806 6.45003C0.0819442 6.59589 0 6.79372 0 7C0 7.20628 0.0819442 7.40411 0.227806 7.54997C0.373667 7.69583 0.571498 7.77778 0.777778 7.77778H6.22222V13.2222C6.22222 13.4285 6.30417 13.6263 6.45003 13.7722C6.59589 13.9181 6.79372 14 7 14C7.20628 14 7.40411 13.9181 7.54997 13.7722C7.69583 13.6263 7.77778 13.4285 7.77778 13.2222V7.77778H13.2222C13.4285 7.77778 13.6263 7.69583 13.7722 7.54997C13.9181 7.40411 14 7.20628 14 7C14 6.79372 13.9181 6.59589 13.7722 6.45003C13.6263 6.30417 13.4285 6.22222 13.2222 6.22222Z" fill="#1C64F2"/></svg>'
    }
  ]
  public socialMediaCollections = signal<SocialCollection[]>([])
  public socialMediaCollectionsDropdown?: ComponentWithDataSetter<SocialMediaCollectionsDropdownComponent>
  public isPresetsCollectionsDropdownOpen = signal(false)
  public isUpdateCollectionDropdownOpen = signal(false)
  public isDataChanged = signal(false)
  public isLoadingCollection = signal(false)
  public selectedCollectionIndex = signal<number | null>(null)
  public isLoading = signal(false)
  public readonly saveAsCollectionModalId = 'save-preset-collection-modal'
  public readonly renameCollectionModalId = 'rename-preset-collection-modal'
  public readonly deleteCollectionModalId = 'delete-preset-collection-modal'
  public collectionNameControl = new FormControl('')
  private _destroySocialMediaCollections$: Subject<void> = new Subject<void>()
  @ViewChild('socialMediaDropdown') socialMediaDropdownRef!: DropdownComponent

  private readonly urlPattern = /^(|https?:\/\/[^\s]+|www\.[^\s]+|[\w][\w.-]*\.[\w.-]+(\/[^\s]*)?)$/
  private _presetsLoadedForWorkspace: number | undefined

  constructor(
    private readonly _eventService: EventService,
    private readonly _modalService: ModalService,
    private readonly _alertService: AlertService,
    private readonly _destroyRef: DestroyRef
  ) {
    effect(() => {
      if (!this.event()) {
        this._presetsLoadedForWorkspace = undefined
        return
      }
      this._setLinks()
      if (this._presetsLoadedForWorkspace === this.event()?.workspace_id) return
      this._presetsLoadedForWorkspace = this.event()?.workspace_id
      this._getPresetsCollections()
    })
  }

  /**
   * Reverse collections when needed:
   * - backend returns oldest -> newest
   * - UI needs newest -> oldest
   * - payload needs to be sent back in backend order, so we reverse again
   * @param collections {SocialCollection[]} - The collections to order
   * @returns {SocialCollection[]} - The ordered collections
   */
  private _orderCollectionsForDisplay(collections: SocialCollection[] = []): SocialCollection[] {
    return collections.length > 1 ? [...collections].reverse() : collections
  }

  /**
   * Get the presets collections for the event
   */
  private _getPresetsCollections(): void {
    this._eventService
      .getPresetsCollections(this.event()?.workspace_id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((collections) => {
        const orderedCollections = this._orderCollectionsForDisplay(collections.data.social_collection)
        this.socialMediaCollections.set(orderedCollections)
        this._createSocialMediaCollectionsDropdown(orderedCollections)
      })
  }

  private _createSocialMediaCollectionsDropdown(collections: SocialCollection[]): void {
    this._destroySocialMediaCollections$.next()
    this._destroySocialMediaCollections$.complete()
    this._destroySocialMediaCollections$ = new Subject<void>()
    this.socialMediaCollectionsDropdown = {
      component: SocialMediaCollectionsDropdownComponent,
      dataSetter: (instance: SocialMediaCollectionsDropdownComponent) => {
        instance.collections.set(collections)
        instance.linkEmptyIcon.set(this.linkEmptyIcon)
        instance.workspaceId.set(this.event()?.workspace_id ?? 0)
        instance.onOptionClicked.pipe(takeUntil(this._destroySocialMediaCollections$)).subscribe((index) => {
          this.socialMediaDropdownRef.toggleDropdown()
          this.onPresetsCollectionOptionClicked(collections[index])
          this.selectedCollectionIndex.set(index)
        })
        instance.onRenameCollection.pipe(takeUntil(this._destroySocialMediaCollections$)).subscribe((index) => {
          this.socialMediaDropdownRef.toggleDropdown()
          this.collectionNameControl.setValue(collections[index].name)
          this.selectedCollectionIndex.set(index)
          this._modalService.toggleModal(this.renameCollectionModalId)
        })
        instance.onDeleteCollection.pipe(takeUntil(this._destroySocialMediaCollections$)).subscribe((index) => {
          this.socialMediaDropdownRef.toggleDropdown()
          this.selectedCollectionIndex.set(index)
          this._modalService.toggleModal(this.deleteCollectionModalId)
        })
      }
    }
  }

  /**
   * Delete a collection
   * @param collection {SocialCollection} - The collection to delete
   */
  public confirmDeletingPresetCollection(): void {
    const collections = [...this.socialMediaCollections()]
    collections.splice(this.selectedCollectionIndex() ?? 0, 1)
    this._eventService
      .deletePresetsCollections(this.event()?.workspace_id, this._orderCollectionsForDisplay(collections))
      .pipe(
        finalize(() => {
          this.selectedCollectionIndex.set(null)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response?.message || 'Collection deleted successfully')
          const orderedCollections = this._orderCollectionsForDisplay(response.data.social_collection)
          this.socialMediaCollections.set(orderedCollections)
          this._createSocialMediaCollectionsDropdown(orderedCollections)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to delete collection')
        }
      })
  }

  public cancelDeletePresetCollection(): void {
    this.selectedCollectionIndex.set(null)
    if (this.isModalOpened(this.deleteCollectionModalId)) this._modalService.toggleModal(this.deleteCollectionModalId)
  }

  /**
   * Validate the URL for the link control
   * @returns {ValidatorFn} - The validator function
   */
  private _urlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim() ?? ''
      if (!value || this.urlPattern.test(value)) return null
      return { invalidUrl: 'This is not a valid URL. Check the format and try again.' }
    }
  }

  /**
   * Set the links for the event
   */
  private _setLinks(): void {
    this.links.set(this.event()?.social_media.map((link) => ({ ...link, isDragging: false, isLoading: false })) ?? [])
  }

  /**
   * Handle keydown event for the link control
   * @param event {KeyboardEvent} - The keyboard event
   */
  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.linkControl.markAsTouched()
      this.createSocialLink()
    }
  }

  /**
   * Create a social link for the event
   */
  public createSocialLink(): void {
    if (this.linkControl.invalid || !this.linkControl.value || this.isLoading()) return
    const newLink: SocialMediaCard = {
      title: '',
      url: '',
      icon: '',
      isDragging: false,
      isLoading: true
    }
    this.links.update((prev) => [...prev, newLink])
    this.isLoading.set(true)
    this._eventService
      .createEventSocialLink(this.event()?.workspace_id, this.event()?.id, this.linkControl.value)
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          newLink.isLoading = false
          this.linkControl.reset()
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.isDataChanged.set(true)
          this.links.update((prev) => {
            const next = [...prev]
            next[next.length - 1] = { ...res.data, isDragging: false, isLoading: false }
            return next
          })
        }
      })
  }

  /**
   * Remove a social link from the event
   * @param index {number} - The index of the social link
   */
  public onRemoveLink(index: number): void {
    const currentLinks = this.links()
    if (currentLinks[index].isLoading || this.isLoading()) return
    this.links.update((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], isLoading: true }
      return next
    })
    this.isLoading.set(true)
    const social_media = [...currentLinks]
    social_media.splice(index, 1)
    this._eventService
      .updateEventData(this.event()?.workspace_id, this.event()?.id, { social_media })
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.isDataChanged.set(true)
          this.links.update((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
          })
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to remove social media link')
        }
      })
  }

  /**
   * Update the title of a social link
   * @param index {number} - The index of the social link
   * @param title {string} - The new title
   */
  public onUpdateTitle(index: number, title: string): void {
    const currentLinks = this.links()
    if (currentLinks[index].isLoading || this.isLoading() || currentLinks[index].title === title) return
    this.links.update((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], isLoading: true }
      return next
    })
    this.isLoading.set(true)
    const social_media = currentLinks.map((l, i) => (i === index ? { ...l, title } : l))

    this._eventService
      .updateEventData(this.event()?.workspace_id, this.event()?.id, { social_media })
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          this.links.update((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], isLoading: false }
            return next
          })
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.isDataChanged.set(true)
          this.links.update((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], title }
            return next
          })
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to update social media link title')
        }
      })
  }

  /**
   * Drop a social link in the event
   * @param event {CdkDragDrop<unknown>} - The drag drop event
   */
  public drop(event: CdkDragDrop<unknown>): void {
    const currentLinks = this.links()
    if (event.previousIndex === event.currentIndex || !currentLinks?.length || this.isLoading()) return
    const reordered = [...currentLinks]
    moveItemInArray(reordered, event.previousIndex, event.currentIndex)
    this.links.set(reordered.map((l, i) => (i === event.currentIndex ? { ...l, isLoading: true } : l)))
    this.isLoading.set(true)
    this._eventService
      .updateEventData(this.event()?.workspace_id, this.event()?.id, { social_media: reordered })
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          this.links.update((prev) => {
            const next = [...prev]
            next[event.currentIndex] = { ...next[event.currentIndex], isLoading: false }
            return next
          })
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.isDataChanged.set(true)
        },
        error: (error) => {
          const rolledBack = [...this.links()]
          moveItemInArray(rolledBack, event.currentIndex, event.previousIndex)
          this.links.set(rolledBack)
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to reorder social media links')
        }
      })
  }

  /**
   * Toggle dragging for a social media link
   * @param index {number} - The index of the social media link
   * @param enable {boolean} - Whether to enable dragging
   */
  public toggleDragging(index: number, enable: boolean = false): void {
    if (this.links()?.length === 1) return
    this.links.update((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], isDragging: enable }
      return next
    })
  }

  /**
   * Handle the click event for a presets collection option
   * @param option {DropdownOption} - The dropdown option
   */
  public onPresetsCollectionOptionClicked(collection: SocialCollection): void {
    if (this.isLoadingEvent() || this.isLoadingCollection()) return
    this.isLoadingCollection.set(true)

    this._eventService
      .updateEventData(this.event()?.workspace_id, this.event()?.id, { social_media: collection.social_media })
      .pipe(
        finalize(() => {
          this.isLoadingCollection.set(false)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.links.set(collection.social_media.map((link) => ({ ...link, isDragging: false, isLoading: false })))
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to load collection')
        }
      })
  }

  /**
   * Handle the open change event for the presets collections dropdown
   * @param open {boolean} - Whether the dropdown is open
   */
  public onOpenCollectionDropdownChange(open: boolean): void {
    this.isPresetsCollectionsDropdownOpen.set(open)
  }

  /**
   * Handle the open change event for the update collection dropdown
   * @param open {boolean} - Whether the dropdown is open
   */
  public onOpenUpdateCollectionDropdownChange(open: boolean): void {
    this.isUpdateCollectionDropdownOpen.set(open)
  }

  /**
   * Toggle the save as collection modal
   */
  public toggleSaveAsCollectionModal(): void {
    this.collectionNameControl.reset()
    this._modalService.toggleModal(this.saveAsCollectionModalId)
  }

  public isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  /**
   * Handle the keydown event for the collection name control
   * @param event {KeyboardEvent} - The keyboard event
   */
  public onKeydownCollectionName(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSaveAsCollection()
    }
  }

  /**
   * Handle the keydown event for the collection name control
   * @param event {KeyboardEvent} - The keyboard event
   */
  public onKeydownRenameCollectionName(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onRenameCollection()
    }
  }

  /**
   * Save the collection as a new collection
   */
  public onSaveAsCollection(): void {
    const name = this.collectionNameControl.value?.trim()

    if (this.isLoading() || !name) return
    this.isLoading.set(true)
    this._eventService
      .createPresetsCollections(this.event()?.workspace_id, name, this.links())
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          this.toggleSaveAsCollectionModal()
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          const updatedCollections = this._orderCollectionsForDisplay(res.data.social_collection ?? [])
          this.socialMediaCollections.set(updatedCollections)
          this._createSocialMediaCollectionsDropdown(updatedCollections)
          this.isDataChanged.set(false)
          this.selectedCollectionIndex.set(null)
          this._alertService.alert(Alert.SUCCESS, res?.message || 'Collection saved successfully')
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to save collection')
        }
      })
  }

  /**
   * Save the collection as a new collection
   */
  public onRenameCollection(): void {
    const name = this.collectionNameControl.value?.trim()
    this.socialMediaCollections.update((prev) => {
      const next = [...prev]
      next[this.selectedCollectionIndex()] = { ...next[this.selectedCollectionIndex()], name }
      return next
    })
    this.onUpdateCollection()
  }

  /**
   * Rename a collection
   * @param collection {SocialCollection} - The collection to rename
   */
  public onUpdateCollection(): void {
    if (this.isLoading()) return
    this.isLoading.set(true)
    this._eventService
      .updatePresetsCollections(this.event()?.workspace_id, this._orderCollectionsForDisplay(this.socialMediaCollections()))
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          if (this.isModalOpened(this.saveAsCollectionModalId)) {
            this.toggleSaveAsCollectionModal()
          }
          if (this.isModalOpened(this.renameCollectionModalId)) {
            this._modalService.toggleModal(this.renameCollectionModalId)
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.selectedCollectionIndex.set(null)
          this.isDataChanged.set(false)
          const orderedCollections = this._orderCollectionsForDisplay(res.data.social_collection ?? [])
          this.socialMediaCollections.set(orderedCollections)
          this._createSocialMediaCollectionsDropdown(orderedCollections)
          this._alertService.alert(Alert.SUCCESS, res?.message || 'Collection updated successfully')
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message || 'Failed to update collection')
        }
      })
  }

  /**
   * Handle the click event for an update collection option
   * @param option {DropdownOption} - The dropdown option
   */
  public onUpdateCollectionOptionClicked(option: DropdownOption): void {
    switch (option.key) {
      case UpdateCollectionDropdownOptions.UPDATE: {
        this.socialMediaCollections()[this.selectedCollectionIndex()].social_media = this.links().map((link) => ({
          title: link.title,
          url: link.url,
          icon: link.icon
        }))
        this.onUpdateCollection()
        break
      }
      case UpdateCollectionDropdownOptions.ADD: {
        this.toggleSaveAsCollectionModal()
        break
      }
    }
  }
}
